import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios, { AxiosResponse } from 'axios';
import pick from 'lodash.pick';
import fileDownload from 'js-file-download';

// Global Types
import { SagaIterator } from 'redux-saga';

// Utils
import formatters from 'forms/formatters';

import { PersonModal } from 'scenes/Workforce/scenes/People/constants';

// Constants
import { format } from 'date-fns';
import { EXPORT_PEOPLE_MODAL } from 'scenes/Workforce/scenes/People/components/ExportPeopleModal';
import qs from 'qs';
import { IMPORT_PEOPLE_MODAL } from 'scenes/Organizations/components/ImportPeopleModal';
import {
    PERSON_CREATE_REQUESTED,
    PERSON_UPDATE_REQUESTED,
    PERSON_DELETE_REQUESTED,
    PERSON_GROUPED_PEOPLE_EXPORT_REQUESTED,
    PERSON_GROUPED_PEOPLE_IMPORT_REQUESTED
} from './constants';

// Local Types
import { Person } from './types';

// Actions
// @ts-ignore
import { enqueueSnackbar } from '../notifications/actions';
import { updateQueryString } from '../router/actions';
import { addedGroupsToPerson } from '../groupPeople/actions';
import {
    personCreated,
    failedCreatePerson,
    personEdited,
    failedEditPerson,
    personDeleted,
    failedDeletePerson,
    succeededExportGroupedPeople,
    failedExportGroupedPeople,
    succeededImportGroupedPeople,
    failedImportGroupedPeople
    // @ts-ignore
} from './actions';
// @ts-ignore
import { resetState } from '../auth/actions';

// Selectors
import { selectIsAdmin, makeSelectUsersMeValue } from '../usersMe/selectors';
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
// @ts-ignore
import { selectPeopleKeyedById } from './selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postPerson({
    data
}: {
    data: Person;
    type: string;
}): SagaIterator {
    const idToken = yield select(idTokenSelector);

    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    const isAdmin = yield select(selectIsAdmin);

    const endpoint = isAdmin ? 'People/withUser' : 'People';

    const url = `${REACT_APP_API_URL}/v1/${endpoint}`;

    const payload = {
        ...pick(data, [
            'firstName',
            'lastName',
            'datacomId',
            'groupIds',
            'email',
            'roleIds',
            'sprayCertification',
            'mobileNumber',
            'isSubscribedToHarvestEmail',
            'isSubscribedToHarvestSms',
            'sectorNotifications'
        ]),
        organizationId
    };

    if (payload.mobileNumber) {
        payload.mobileNumber = formatters.formatMobileNumberOut(
            payload.mobileNumber
        );
    }

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response: AxiosResponse = yield call(
            axios.post,
            url,
            payload,
            config
        );

        yield put(personCreated(response.data));

        yield put(addedGroupsToPerson(response.data.id, data.groupIds));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Person created successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                [PersonModal.PERSON_CREATE_MODAL]: undefined,
                selectedPerson: undefined
            })
        );
    } catch (error) {
        // Failed Create Person
        yield put(failedCreatePerson(error));
    }
}

export function* watchCreatePerson(): SagaIterator {
    yield takeLatest(PERSON_CREATE_REQUESTED, postPerson);
}

export function* patchPerson({
    id,
    data
}: {
    id: number;
    data: Partial<Person>;
    type: string;
}): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const isAdmin = yield select(selectIsAdmin);

    const endpoint = isAdmin ? `People/${id}/withUser` : `People/${id}`;

    const url = `${REACT_APP_API_URL}/v1/${endpoint}`;
    const config = {
        headers: {
            Authorization: idToken
        }
    };
    const payload = pick(data, [
        'firstName',
        'lastName',
        'datacomId',
        'groupIds',
        'email',
        'roleIds',
        'sprayCertification',
        'mobileNumber',
        'isSubscribedToHarvestEmail',
        'isSubscribedToHarvestSms',
        'sectorNotifications'
    ]);

    let isOwnEmailUpdated = false;

    if (payload.mobileNumber) {
        payload.mobileNumber = formatters.formatMobileNumberOut(
            payload.mobileNumber
        );
    }

    if (payload.email) {
        const peopleById = yield select(selectPeopleKeyedById);
        const userId = yield select(makeSelectUsersMeValue('id'));

        if (
            peopleById[id] &&
            peopleById[id].userId &&
            peopleById[id].userId === userId
        ) {
            isOwnEmailUpdated = true;
        }
    }

    try {
        const response: AxiosResponse = yield call(
            axios.patch,
            url,
            payload,
            config
        );

        yield put(personEdited(id, response.data));

        if (data.groupIds) {
            yield put(addedGroupsToPerson(response.data.id, data.groupIds));
        }

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Person updated successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                [PersonModal.PERSON_UPDATE_MODAL]: undefined,
                selectedPerson: undefined
            })
        );

        if (isOwnEmailUpdated) {
            yield put(resetState());
        }
    } catch (error) {
        yield put(failedEditPerson(error));
    }
}

export function* watchEditPerson(): SagaIterator {
    yield takeLatest(PERSON_UPDATE_REQUESTED, patchPerson);
}

export function* deletePerson({
    id
}: {
    id: number;
    type: string;
}): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const isAdmin = yield select(selectIsAdmin);

    const endpoint = isAdmin ? `People/${id}/withUser` : `People/${id}`;

    const url = `${REACT_APP_API_URL}/v1/${endpoint}`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response: AxiosResponse = yield call(axios.delete, url, config);
        yield put(personDeleted(id, response.data));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Person deleted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                [PersonModal.PERSON_DELETE_MODAL]: undefined,
                selectedPerson: undefined
            })
        );
    } catch (error) {
        yield put(failedDeletePerson(error));
    }
}

export function* watchDeletePerson(): SagaIterator {
    yield takeLatest(PERSON_DELETE_REQUESTED, deletePerson);
}

export function* requestedExportGroupedPeople({
    payload
}: {
    payload: Person['id'][];
    type: string;
}): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/People/groupedPeople`;
    const params = {
        peopleIds: payload,
        organizationId
    };

    const config = {
        headers: {
            'Content-Type': 'text/csv; charset=utf-8',
            Accept: 'text/csv',
            Authorization: idToken
        },
        params,
        paramsSerializer: (data: typeof params) => {
            return qs.stringify(data, { arrayFormat: 'repeat' });
        }
    };

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'GET',
            url,
            ...config
        });

        const date = format(new Date(), 'dd-MM-yyyy_HH-mm');

        // File Download
        fileDownload(response.data, `${date}_exported_people.csv`);

        yield put(
            enqueueSnackbar({
                message: 'People exported successfully',
                options: { variant: 'success' }
            })
        );
        // Update Query String
        yield put(
            updateQueryString({
                [EXPORT_PEOPLE_MODAL]: undefined
            })
        );
        yield put(succeededExportGroupedPeople(response));
    } catch (error) {
        yield put(failedExportGroupedPeople(error as any));
    }
}

export function* watchRequestedExportGroupedPeople(): SagaIterator {
    yield takeLatest(
        PERSON_GROUPED_PEOPLE_EXPORT_REQUESTED,
        requestedExportGroupedPeople
    );
}

export function* requestedImportGroupedPeople({
    payload
}: {
    payload: { file: File; organizationIds: string[] };
    type: string;
}): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const organizationId = yield select(selectCurrentOrganizationId);

    const { file, organizationIds } = payload;

    const url = `${REACT_APP_API_URL}/v1/People/importFromCSV`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organizationId', organizationId);
    organizationIds.forEach(orgId => {
        formData.append('organizationIds', orgId);
    });

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: idToken
        }
    };

    try {
        // @ts-ignore
        const response = yield call(axios.post, url, formData, config);

        yield put(
            enqueueSnackbar({
                message: 'People imported successfully',
                options: { variant: 'success' }
            })
        );
        // Update Query String
        yield put(
            updateQueryString({
                [IMPORT_PEOPLE_MODAL]: undefined
            })
        );
        yield put(succeededImportGroupedPeople(response));
    } catch (error) {
        yield put(failedImportGroupedPeople(error as any));
    }
}

export function* watchRequestedImportGroupedPeople(): SagaIterator {
    yield takeLatest(
        PERSON_GROUPED_PEOPLE_IMPORT_REQUESTED,
        requestedImportGroupedPeople
    );
}
